import React from "react";
import Typography from "aldoo-ra/Typography";
import Input from "aldoo-ra/Input";
import Checkbox from "aldoo-ra/Checkbox";
import FieldSelector from "./components/FieldSelector";

/**
 * Rename Field Plugin
 * Renames a field across content type and all items
 */
export default {
  id: "cms-rename-field",
  name: "Rename Field",
  description: "Rename a field across all items",
  category: "type-operations",
  hidden: true,

  requiredCapabilities: {
    needsContentType: true,
    needsSelection: false,
    needsFieldAccess: true,
    needsTypeModification: true,
  },

  validate(context) {
    const errors = [];

    if (!context.contentType) {
      errors.push({ message: "Content type is required" });
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  getInterface(context) {
    const fields = context.typeUtils.getAllFields();

    return {
      dialog: {
        title: "Rename Field",
        fields: [
          {
            id: "oldFieldName",
            type: "custom",
            label: "Field to Rename",
            required: true,
            render: (props) => (
              <FieldSelector
                value={props.value}
                onChange={props.onChange}
                fields={fields}
                multiple={false}
                label="Field to Rename"
                required={true}
                error={props.error}
              />
            ),
          },
          {
            id: "newFieldName",
            type: "custom",
            label: "New Field Name",
            required: true,
            render: (props) => (
              <div>
                <Typography variant="admin_label" className="mb-1">
                  New Field Name
                  <span className="text-admin_error ml-1">*</span>
                </Typography>
                <Input
                  value={props.value || ''}
                  onChange={(e) => props.onChange(e.target.value)}
                  placeholder="New field name"
                  variant="admin"
                  className="w-full"
                />
                {props.error && (
                  <Typography variant="admin_caption" className="text-admin_error mt-1">
                    {props.error}
                  </Typography>
                )}
              </div>
            ),
            validate: (value, allValues) => {
              if (!value || !value.trim()) {
                return "New field name is required";
              }
              if (value === allValues.oldFieldName) {
                return "New name must be different";
              }
              if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(value)) {
                return "Field name must start with letter or underscore";
              }
              if (context.typeUtils.getFieldDefinition(value)) {
                return "Field with this name already exists";
              }
              return null;
            },
          },
          {
            id: "updateReferences",
            type: "checkbox",
            label: "Update references in other fields (if any)",
            defaultValue: true,
          },
        ],
      },

      summary: {
        beforeExecute: (values, context) => {
          // Count items that have this field
          const itemsWithField = context.selectedItems
            ? context.selectedItems.filter((item) => item.content?.[values.oldFieldName] !== undefined)
                .length
            : 0;

          const warnings = [];
          if (itemsWithField === 0) {
            warnings.push("No items currently have this field");
          }

          return {
            itemsAffected: itemsWithField,
            fieldsAffected: [values.oldFieldName, values.newFieldName],
            changes: [
              {
                description: `Rename field "${values.oldFieldName}" to "${values.newFieldName}" in type definition`,
                impact: "high",
              },
              {
                description: `Update ${itemsWithField} item(s) with renamed field`,
                impact: "high",
              },
            ],
            warnings,
          };
        },

        afterExecute: (result) => {
          if (result.success) {
            return `Successfully renamed field in ${result.itemsSucceeded} item(s) and updated type definition`;
          }
          return `Renamed field in ${result.itemsSucceeded} item(s), ${result.itemsFailed} failed`;
        },
      },
    };
  },

  async execute(context, params) {
    const { values } = params;
    const { oldFieldName, newFieldName } = values;

    try {
      // Update type definition
      const updatedTypeDefinition = context.contentType.type_definition.map((field) => {
        if (field.name === oldFieldName) {
          return { ...field, name: newFieldName };
        }
        return field;
      });

      await context.controller.updateContentType(context.contentType._id, {
        type_definition: updatedTypeDefinition,
      });

      // Get all items of this content type
      const allItems = await context.controller.getContentList(context.contentType._id);

      let itemsSucceeded = 0;
      let itemsFailed = 0;
      const errors = [];

      context.ui.showProgress(0, allItems.length, "Renaming field...");

      for (let i = 0; i < allItems.length; i++) {
        const item = allItems[i];

        // Only update items that have the old field
        if (item.content?.[oldFieldName] !== undefined) {
          try {
            const updatedContent = { ...item.content };
            updatedContent[newFieldName] = updatedContent[oldFieldName];
            delete updatedContent[oldFieldName];

            const result = await context.controller.saveContent({
              _id: item._id,
              content: updatedContent,
            }, false);

            if (result?.error) {
              itemsFailed++;
              errors.push({
                itemId: item._id,
                error: result.error,
              });
            } else {
              itemsSucceeded++;
            }
          } catch (error) {
            itemsFailed++;
            errors.push({
              itemId: item._id,
              error: error.message,
            });
          }
        }

        context.ui.showProgress(i + 1, allItems.length, "Renaming field...");
      }

      return {
        success: itemsFailed === 0,
        itemsProcessed: allItems.length,
        itemsSucceeded,
        itemsFailed,
        errors: errors.length > 0 ? errors : undefined,
      };
    } catch (error) {
      return {
        success: false,
        itemsProcessed: 0,
        itemsSucceeded: 0,
        itemsFailed: 0,
        errors: [{ error: error.message }],
      };
    }
  },
};
